import { Box, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Fragment, useRef, useState } from 'react';
import { Close as CloseIcon, WhatsApp as WhatsAppIcon, KeyboardArrowDown, KeyboardArrowUp, Search as SearchIcon, Cake as CakeIcon, PersonAdd as PersonAddIcon, Edit as EditIcon, DescriptionOutlined as DescriptionIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../shared/forms';
import * as Yup from 'yup';
import { VTextField, VTextFieldPhone } from '../../../shared/forms';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { clientService, feedback, IClientView } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { dateFormat } from '../../../shared/helpers/dateFormat';
import { Button } from '../../../shared/components/MUI/button/Button';
import { CreateClientModal } from '../../../shared/components/SconApp/createClientModal/CreateClientModal';
import { usePagination } from '../../../shared/hooks/usePagination';
import { SconTablePagination } from '../../../shared/components/UI';
import { FilterBuilder, FilterChips } from '../../../shared/components/SconApp/filterBuilder/FilterBuilder';
import { FilterField } from '../../../shared/components/SconApp/filterBuilder/filterTypes';
import { useFilterBuilder } from '../../../shared/hooks/useFilterBuilder';

const clientFilterFields: FilterField[] = [
  { key: 'lastVisit', label: 'Última visita', operators: [{ value: 'gt', label: 'há mais de' }, { value: 'lt', label: 'há menos de' }], valueType: 'number', suffix: 'dias' },
  { key: 'totalAppointments', label: 'Atendimentos', operators: [{ value: 'gte', label: '≥' }, { value: 'lte', label: '≤' }], valueType: 'number', suffix: 'vezes' },
  { key: 'whatsAppConfirmed', label: 'WhatsApp confirmado', operators: [{ value: 'eq', label: 'é' }], valueType: 'boolean', booleanOptions: [{ label: 'Sim', value: true }, { label: 'Não', value: false }] },
];

export const BusinessClients: React.FC<IPropsBusinessPage> = ({ business }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const pagination = usePagination();
  const [expandedClient, setExpandedClient] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'clients' | 'birthdays'>('clients');
  const [modalOpen, setModalOpen] = useState(false);
  const [editClient, setEditClient] = useState<IClientView | null>(null);
  const [notesClient, setNotesClient] = useState<IClientView | null>(null);
  const [whatsAppDialogPhone, setWhatsAppDialogPhone] = useState<string | null>(null);
  const filters = useFilterBuilder();
  const queryClient = useQueryClient();

  const handleSearchChange = (value: string) => {
    setSearch(value);
    pagination.resetPage();
    const timeout = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(timeout);
  };

  const filterParams = filters.toFilterParams();
  const { data: pagedClients, isFetching } = useQuery({
    queryKey: ['clients', business.id, pagination.page, pagination.rowsPerPage, debouncedSearch, filters.toQueryKey()],
    queryFn: () => clientService.getClients(business.id, pagination.page + 1, pagination.rowsPerPage, debouncedSearch || undefined, filterParams),
  });
  const clients = pagedClients?.items;
  const totalCount = pagedClients?.totalCount ?? 0;

  const { data: clientHistory, isFetching: isFetchingHistory } = useQuery({
    queryKey: ['clientHistory', expandedClient, business.id],
    queryFn: () => clientService.getHistory(expandedClient!, business.id),
    enabled: !!expandedClient && expandedClient > 0,
  });

  const { data: birthdays } = useQuery({
    queryKey: ['clientBirthdays', business.id],
    queryFn: () => clientService.getBirthdays(business.id),
  });

  const marketingMutation = useMutation({
    mutationFn: ({ clientId, businessId, allowMarketing }: { clientId: number; businessId: number; allowMarketing: boolean }) =>
      clientService.updateMarketing(clientId, businessId, allowMarketing),
    onSuccess: () => {
      queryClient.invalidateQueries(['clients', business.id]);
    },
  });

  const openWhatsApp = (phone: string | undefined) => {
    if (!phone) return;
    const digits = phone.replace(/\D/g, '');
    window.open(`https://wa.me/55${digits}`, '_blank');
  };

  const handleWhatsAppClick = (phone: string | undefined, phoneConfirmed: boolean | undefined) => {
    if (!phone) return;
    if (phoneConfirmed) {
      openWhatsApp(phone);
    } else {
      setWhatsAppDialogPhone(phone);
    }
  };

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={viewMode}
          exclusive
          onChange={(_, value) => value && setViewMode(value)}
        >
          <ToggleButton value='clients'>Clientes</ToggleButton>
          <ToggleButton value='birthdays'>
            <CakeIcon sx={{ mr: 0.5, fontSize: 16 }} /> Aniversariantes
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant='contained'
          startIcon={<PersonAddIcon />}
          onClick={() => setModalOpen(true)}
          size='small'
        >
          Novo cliente
        </Button>
      </Box>

      {viewMode === 'clients' && (
        <Box display='flex' flexDirection='column' gap={2}>
          <Box display='flex' gap={1} alignItems='center'>
            <FilterBuilder
              fields={clientFilterFields}
              onApply={(state) => {
                filters.setRows(state.rows, state.operator);
                pagination.resetPage();
              }}
              onClear={() => { filters.clearAll(); pagination.resetPage(); }}
              activeFilters={filters.activeFilterCount}
            />
            <TextField
              size='small'
              placeholder='Buscar cliente por nome, telefone ou email...'
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ flex: 1 }}
            />
          </Box>

          {filters.hasActiveFilters && (
            <FilterChips
              rows={filters.state.rows}
              fields={clientFilterFields}
              onRemove={(id) => { filters.removeRow(id); pagination.resetPage(); }}
              onClear={() => { filters.clearAll(); pagination.resetPage(); }}
            />
          )}

          {isFetching ? (
            <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
          ) : clients && clients.length > 0 ? (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>Ações</TableCell>
                      <TableCell />
                      <TableCell>Cliente</TableCell>
                      <TableCell>Telefone</TableCell>
                      <TableCell align='right'>Atendimentos</TableCell>
                      <TableCell>Último</TableCell>
                      <TableCell align='center'>Marketing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client) => {
                      const rowKey = client.clientId ?? client.userId ?? 0;
                      const expandKey = client.userId ?? -(client.clientId);
                      const hasUser = !!client.userId && client.userId > 0;
  
                      return (
                        <Fragment key={rowKey}>
                          <TableRow hover>
                            <TableCell>
                              {!hasUser && (
                                <IconButton size='small' onClick={() => setEditClient(client)}>
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              )}
                              {client.phone && (
                                <Tooltip
                                  title={client.phoneConfirmed
                                    ? 'Abrir conversa no WhatsApp'
                                    : 'Telefone não confirmado pelo cliente. Clique para enviar mesmo assim.'}
                                >
                                  <span>
                                    <IconButton
                                      size='small'
                                      onClick={() => handleWhatsAppClick(client.phone, client.phoneConfirmed)}
                                    >
                                      <WhatsAppIcon
                                        fontSize='small'
                                        color={client.phoneConfirmed ? 'success' : 'disabled'}
                                      />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {client.notes && (
                                <Tooltip title='Ver observação'>
                                  <IconButton size='small' onClick={() => setNotesClient(client)}>
                                    <DescriptionIcon fontSize='small' color='action' />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size='small'
                                disabled={!hasUser}
                                onClick={() => hasUser ? setExpandedClient(expandedClient === expandKey ? null : expandKey) : undefined}
                              >
                                {expandedClient === expandKey ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2' fontWeight={500}>{client.name}</Typography>
                              {client.email && <Typography variant='caption' color='text.secondary'>{client.email}</Typography>}
                            </TableCell>
                            <TableCell>{client.phone || '—'}</TableCell>
                            <TableCell align='right'>{client.totalAppointments}</TableCell>
                            <TableCell>{client.lastAppointment ? dateFormat(client.lastAppointment, 'DD/MM/YYYY') : '—'}</TableCell>
                            <TableCell align='center'>
                              {client.clientId > 0 ? (
                                <Switch
                                  size='small'
                                  checked={client.allowMarketing}
                                  onChange={(e) =>
                                    marketingMutation.mutate({
                                      clientId: client.clientId,
                                      businessId: business.id,
                                      allowMarketing: e.target.checked,
                                    })
                                  }
                                />
                              ) : (
                                <Typography variant='caption' color='text.secondary'>—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow key={`${rowKey}-history`}>
                            <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedClient === expandKey ? undefined : 'none' }}>
                              <Collapse in={expandedClient === expandKey} timeout='auto' unmountOnExit>
                                <Box p={2} bgcolor='action.hover'>
                                  {isFetchingHistory ? (
                                    <Box display='flex' justifyContent='center' py={2}><CircularProgress size={24} /></Box>
                                  ) : clientHistory && clientHistory.length > 0 ? (
                                    <Table size='small'>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>#</TableCell>
                                          <TableCell>Data</TableCell>
                                          <TableCell>Serviço</TableCell>
                                          <TableCell>Profissional</TableCell>
                                          <TableCell>Pagamento</TableCell>
                                          <TableCell>Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {clientHistory.map((item) => (
                                          <TableRow key={item.appointmentId}>
                                            <TableCell>#{item.appointmentNumber}</TableCell>
                                            <TableCell>{dateFormat(item.date, 'DD/MM')}</TableCell>
                                            <TableCell>{item.serviceName}</TableCell>
                                            <TableCell>{item.employeeName}</TableCell>
                                            <TableCell>{item.paymentMethodName}</TableCell>
                                            <TableCell>
                                              <Chip
                                                size='small'
                                                label={item.canceled ? 'Cancelado' : item.completed ? 'Finalizado' : 'Pendente'}
                                                color={item.canceled ? 'error' : item.completed ? 'success' : 'default'}
                                                variant='outlined'
                                              />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    <Typography variant='caption' color='text.secondary'>Nenhum histórico encontrado.</Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <SconTablePagination
                count={totalCount}
                {...pagination.tablePaginationProps}
              />
            </>
          ) : (
            <Box display='flex' justifyContent='center' py={6}>
              <Typography color='text.secondary'>
                {debouncedSearch ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado.'}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {viewMode === 'birthdays' && (
        <Box display='flex' flexDirection='column' gap={2}>
          {!birthdays ? (
            <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
          ) : birthdays.length === 0 ? (
            <Box display='flex' justifyContent='center' py={6}>
              <Typography color='text.secondary'>Nenhum aniversariante no próximo mês.</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>Ações</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Aniversário</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {birthdays.map((b) => {
                    const bdayDate = b.lastAppointment ? new Date(b.lastAppointment) : null;
                    return (
                      <TableRow key={b.userId ?? b.clientId}>
                        <TableCell>
                          {b.phone && (
                            <Tooltip
                              title={b.phoneConfirmed
                                ? 'Abrir conversa no WhatsApp'
                                : 'Telefone não confirmado pelo cliente. Clique para enviar mesmo assim.'}
                            >
                              <span>
                                <IconButton
                                  size='small'
                                  onClick={() => handleWhatsAppClick(b.phone, b.phoneConfirmed)}
                                >
                                  <WhatsAppIcon
                                    fontSize='small'
                                    color={b.phoneConfirmed ? 'success' : 'disabled'}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' fontWeight={500}>{b.name}</Typography>
                          {b.email && <Typography variant='caption' color='text.secondary'>{b.email}</Typography>}
                        </TableCell>
                        <TableCell>
                          {bdayDate && (
                            <Chip
                              icon={<CakeIcon />}
                              label={dateFormat(bdayDate, 'DD/MM')}
                              size='small'
                              color='primary'
                              variant='outlined'
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      <CreateClientModal
        open={modalOpen}
        businessId={business.id}
        onClose={() => setModalOpen(false)}
      />

      <EditClientModal
        client={editClient}
        businessId={business.id}
        onClose={() => setEditClient(null)}
      />

      <Dialog
        open={!!whatsAppDialogPhone}
        onClose={() => setWhatsAppDialogPhone(null)}
      >
        <DialogTitle>WhatsApp não confirmado</DialogTitle>
        <DialogContent>
          <Typography>
            Este WhatsApp não foi confirmado pelo cliente. Deseja enviar mensagem mesmo assim?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' variant='outlined' onClick={() => setWhatsAppDialogPhone(null)} />
          <Button
            label='Enviar mesmo assim'
            variant='contained'
            onClick={() => {
              openWhatsApp(whatsAppDialogPhone!);
              setWhatsAppDialogPhone(null);
            }}
          />
        </DialogActions>
      </Dialog>

      <NotesDialog
        client={notesClient}
        onClose={() => setNotesClient(null)}
      />
    </Box>
  );
};

interface NotesDialogProps {
  client: IClientView | null;
  onClose: () => void;
}

const NotesDialog: React.FC<NotesDialogProps> = ({ client, onClose }) => {
  if (!client || !client.notes) return null;

  return (
    <Dialog open={!!client} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant='h6'>Observação — {client.name}</Typography>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
          {client.notes}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

interface EditClientModalProps {
  client: IClientView | null;
  businessId: number;
  onClose: () => void;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  email: Yup.string().email('E-mail inválido').nullable(),
  phone: Yup.string().nullable(),
});

const EditClientModal: React.FC<EditClientModalProps> = ({ client, businessId, onClose }) => {
  const formRef = useRef<FormHandles>(null);
  const queryClient = useQueryClient();

  const { mutate: updateClient, isLoading } = useMutation({
    mutationFn: (data: { name: string; email: string; phone: string; notes?: string }) =>
      clientService.updateClient({
        clientId: client!.clientId,
        businessId,
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      feedback('Cliente atualizado com sucesso!', 'success');
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
      onClose();
    },
    onError: (error: Error) => feedback(error.message, 'error'),
  });

  const handleSubmit = async (data: { name: string; email: string; phone: string; notes?: string }) => {
    try {
      formRef.current?.setErrors({});
      await schema.validate(data, { abortEarly: false });
      updateClient(data);
    } catch (err: unknown) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  };

  if (!client) return null;

  return (
    <Dialog open={!!client} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant='h6'>Editar cliente</Typography>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={{ name: client.name, email: client.email || '', phone: client.phone || '', notes: client.notes || '' }}>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <VTextField name='name' label='Nome completo' required fullWidth />
            <VTextField name='email' label='E-mail' type='email' fullWidth />
            <VTextFieldPhone name='phone' label='Telefone' fullWidth />
            <VTextField name='notes' label='Observação' multiline rows={3} fullWidth />
            <Box display='flex' justifyContent='flex-end' gap={1} mt={1}>
              <Button label='Cancelar' variant='outlined' onClick={onClose} />
              <Button label='Salvar' variant='contained' type='submit' isLoading={isLoading} />
            </Box>
          </Box>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
